import React, { useState, useRef, useEffect } from "react";
import { detectQRFromVideo } from "./utils/detectQR";
import "./style/App.css";

const App = () => {
  const [streaming, setStreaming] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [detectionTriggered, setDetectionTriggered] = useState(false);
  const [videoClosing, setVideoClosing] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [introVisible, setIntroVisible] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionInterval = useRef(null);
  const contentVideoRef = useRef(null);
  const lastDetectedCode = useRef(null);

  // Map video id (from QR code URL ?id=<name>) to video files.
  // QR codes should encode a URL like: https://yolo-artwork-detction.pages.dev/?id=birds
  const videoMapping = {
    "birds": "birds.mp4",
    "flower": "flower.mp4",
    "ships": "ships.mp4",
    "wave": "wave.mp4",
    "seagull": "seagull.mp4",
  };

  // Pause QR scanning
  const pauseDetection = () => {
    if (detectionInterval.current) {
      clearInterval(detectionInterval.current);
      detectionInterval.current = null;
    }
  };

  // Resume QR scanning
  const resumeDetection = () => {
    if (streaming && !detectionInterval.current) {
      detectionInterval.current = setInterval(() => {
        if (videoRef.current) {
          detectQRFromVideo(videoRef.current, canvasRef.current, handleQRDetection);
        }
      }, 300);
    }
  };

  const ALLOWED_DOMAIN = "yolo-artwork-detction.pages.dev";

  // Handle a detected QR code
  const handleQRDetection = (data) => {
    // Only accept QR codes from the hard-coded domain
    let code;
    try {
      const url = new URL(data.trim());
      console.log("QR code scanned:", data.trim());
      if (url.hostname !== ALLOWED_DOMAIN) {
        console.log("Ignored: domain not allowed:", url.hostname);
        return;
      }
      code = url.searchParams.get("id");
      console.log("Video id:", code);
    } catch {
      console.log("QR code scanned (invalid URL):", data.trim());
      code = null;
    }
    if (!code) return;

    // Trigger jump and glow animation
    setDetectionTriggered(true);
    setTimeout(() => setDetectionTriggered(false), 1300);

    // Only act if this is a new code
    if (lastDetectedCode.current === code) return;
    lastDetectedCode.current = code;

    const videoFile = videoMapping[code];
    if (!videoFile) return;

    const videoUrl = `${process.env.PUBLIC_URL}/video/${videoFile}`;
    pauseDetection();

    // Wait for animation then show video
    setTimeout(() => {
      setPlayingVideo(videoUrl);

      setTimeout(() => {
        if (contentVideoRef.current) {
          contentVideoRef.current.src = videoUrl;
          contentVideoRef.current.load();
          contentVideoRef.current.play().catch((err) => {
            console.error("Error playing video:", err);
            resumeDetection();
          });
        } else {
          resumeDetection();
        }
      }, 100);
    }, 1300);
  };

  // Toggle introduction panel
  const toggleIntro = () => {
    if (!showIntro) {
      setShowIntro(true);
      setIntroVisible(true);
    } else {
      setShowIntro(false);
    }
  };

  // Close video player
  const closeVideo = () => {
    setVideoClosing(true);
    setTimeout(() => {
      if (contentVideoRef.current) {
        contentVideoRef.current.pause();
        contentVideoRef.current.src = "";
      }
      setPlayingVideo(null);
      setVideoClosing(false);
      lastDetectedCode.current = null;
      resumeDetection();
    }, 600);
  };

  // Start camera stream
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;

        videoRef.current.onloadedmetadata = () => {
          setStreaming(true);

          detectionInterval.current = setInterval(() => {
            if (videoRef.current) {
              detectQRFromVideo(videoRef.current, canvasRef.current, handleQRDetection);
            }
          }, 300);
        };
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Unable to access camera. Please grant camera permissions.");
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (detectionInterval.current) {
      clearInterval(detectionInterval.current);
      detectionInterval.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStreaming(false);

    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="App">
      <div className="header">
        <h1>畫作作品辨識 App</h1>
        {!streaming && showIntro && (
          <div className={`intro-text ${showIntro ? "visible" : ""}`}>
            <p>本 App 使用 QR Code 掃描技術，於使用者開啟攝影機後，能即時辨識出鏡頭前的作品 QR Code，並播放相關介紹影片。</p>
            <p>按下「開始導覽」按鈕以啟動攝影機，將作品旁的 QR Code 置於鏡頭前方，即可觀看該作品之動態影片。</p>
          </div>
        )}
        {!introVisible && !streaming && (
          <button className="start-intro-btn" onClick={toggleIntro}>
            開始導覽
          </button>
        )}
      </div>

      <div className={`content ${detectionTriggered ? "detection-triggered" : ""}`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{ display: streaming ? "block" : "none" }}
        />
        <canvas
          id="canvas"
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Video Player Modal */}
      {playingVideo && (
        <div
          className={`video-modal ${videoClosing ? "closing" : ""}`}
          onClick={closeVideo}
        >
          <div
            className="video-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-video-btn" onClick={closeVideo}>
              ×
            </button>
            <video
              ref={contentVideoRef}
              controls
              autoPlay
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
              onEnded={closeVideo}
            />
          </div>
        </div>
      )}

      <div className="btn-container">
        {introVisible && !streaming ? (
          <button className="start-intro-btn" onClick={startCamera}>
            開始導覽
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default App;
